// services/entryReportingService.ts - Service for Entry (Ticket & Vehicle) Reporting API calls

import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type { ReportQueryParams, PaginatedReportResponse } from "../Component/Application/entries-reporting/types"
import type { TicketReportData, VehicleReportData } from "../Component/Application/entries-reporting/types"
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken")
      window.location.href = "/login"
    }
    return Promise.reject(error)
  },
)

const EntryReportingService = {
  async getTicketReport(
    params: ReportQueryParams & { page?: number; pageSize?: number },
  ): Promise<PaginatedReportResponse<TicketReportData>> {
    try {
      const response = await api.get("/api/entries/reports/tickets/", { params })
      return response.data
    } catch (error) {
      console.error("Error fetching ticket report:", error)
      throw new Error("Failed to fetch ticket report data.")
    }
  },

  async getVehicleReport(
    params: ReportQueryParams & { page?: number; pageSize?: number },
  ): Promise<PaginatedReportResponse<VehicleReportData>> {
    try {
      const response = await api.get("/api/entries/reports/vehicles/", { params })
      return response.data
    } catch (error) {
      console.error("Error fetching vehicle report:", error)
      throw new Error("Failed to fetch vehicle report data.")
    }
  },

  async exportTicketReport(params: ReportQueryParams): Promise<Blob> {
    try {
      const response = await api.get("/api/entries/reports/export/tickets/", { params, responseType: "blob" })
      return response.data
    } catch (error) {
      console.error("Error exporting ticket report:", error)
      throw new Error("Failed to export ticket report.")
    }
  },

  async exportVehicleReport(params: ReportQueryParams): Promise<Blob> {
    try {
      const response = await api.get("/api/entries/reports/export/vehicles/", { params, responseType: "blob" })
      return response.data
    } catch (error) {
      console.error("Error exporting vehicle report:", error)
      throw new Error("Failed to export vehicle report.")
    }
  },
}

export default EntryReportingService
